/**
 * The Budget Recommender's recommendation math, as one pure function the web
 * page (useBudgetRecommendations) and the API (GET /cli/budget-recommend, the
 * MCP's budget_recommend tool) both call. Fetching stays with each caller;
 * everything from "here is the data" to "here are the rows" lives here.
 * BUDGET_RECOMMENDER.md beside the page documents the formulas.
 */
import { PREDEFINED_INITIATIVE_TYPES } from '../kpi/initiative-metrics';
import { allocateBudgets, type AllocationResult } from './allocation';
import { calculateBrandValueBreakdownForRow, calculateModeledRevenueForRow, type AttributionModelLike } from './modeled-revenue';
import { applyRowControls, type RowControls } from './row-controls';
import { confidenceFor, daysSince, deliveryRatioFor, deriveGoogleBidding, normalizePlatformStatus } from './row-utils';
import {
  ANALYSIS_PERIOD_OPTIONS,
  DEFAULT_ANALYSIS_PERIOD,
  type BudgetInputs,
  type EntityCategory,
  type ImpressionShareWindow,
  type PlatformStatus,
  type RecentChange,
  type RecentChangeReason,
  type RecommendationRow,
  type SupportedPlatform,
} from './types';

export const CUBE_CAMPAIGNS = 'company_media_metrics_campaigns';
export const CUBE_ADSETS = 'company_media_metrics_adsets';
export const LAST_UPDATED = 'company_last_updated_date';

/** Days of daily-grain data fetched once; per-row windows aggregate from it */
export const FETCH_WINDOW_DAYS = 30;
/** Extra fetch headroom so YouTube Demand Gen windows — anchored to lagged
 *  YouTube Analytics brand value — can still cover a full 30-day period */
export const BRAND_VALUE_LAG_BUFFER_DAYS = 10;
/** Explicit limit — Cube REST defaults to 10k rows which daily grain can exceed */
export const CUBE_ROW_LIMIT = 50000;

/** CustomAttributionModel.adChannel values (the UI's AdChannel enum). */
export const AD_CHANNEL = {
  META: 'Meta',
  TIKTOK: 'Tiktok',
  GOOGLE_ADS: 'Google Ads',
  YOUTUBE: 'YouTube',
  PINTEREST: 'Pinterest',
} as const;
export type AdChannelValue = (typeof AD_CHANNEL)[keyof typeof AD_CHANNEL];

/** A media target row (GET /custom-attribution/media-targets). */
export interface MediaTargetLike {
  initiativeType: string;
  ad_set_id?: string | null;
  campaign_id?: string | null;
}

/** A live campaign read (GET /paid-media/:platform/campaigns). */
export interface LiveCampaignLike {
  id: string;
  budget_level?: string | null;
  daily_budget?: string | null;
  effective_status?: string | null;
  status?: string | null;
  /**
   * Google only: search impression share over the trailing complete days
   * (fractions; null outside Search/Shopping or when nothing served; absent
   * when the live read could not fetch them). See GoogleBiddingInfo.
   */
  searchImpressionShare?: number | null;
  searchBudgetLostShare?: number | null;
  searchRankLostShare?: number | null;
  impressionShareWindow?: ImpressionShareWindow | null;
}

/** A custom initiative type's category (GET /custom-attribution/initiative-types). */
export interface CustomTypeCategoryLike {
  value: string;
  category: string;
}

/** One recent manual change (GET /budget-recommender/holds). */
export interface RecentChangeHoldLike {
  entityType: string;
  entityId: string;
  platform?: string | null;
  reason: Exclude<RecentChangeReason, 'first-spend'>;
  /** ISO timestamp */
  at: string;
  auditId: string;
  actor: string | null;
  previousDailyBudgetCents?: number;
  newDailyBudgetCents?: number;
}

/**
 * Brand (Demand Generation, measured on Brand ROAS) vs DR (everything else).
 * Predefined types read the shared list; custom types their stored category;
 * anything unknown is Demand Capture — the page's getInitiativeTypeCategory.
 */
export function categoryFor(initiativeType: string, customTypes?: ReadonlyArray<CustomTypeCategoryLike>): EntityCategory {
  const category =
    PREDEFINED_INITIATIVE_TYPES.find((t) => t.value === initiativeType)?.category ??
    customTypes?.find((t) => t.value === initiativeType)?.category ??
    'DEMAND_CAPTURE';
  return category === 'DEMAND_GENERATION' ? 'BRAND' : 'DR';
}

/**
 * Per-channel budget-recommender config, resolved from a Cube `media_channel`.
 * Single source of truth so the display name, ad channel, API platform, and
 * adset-level semantics stay in lockstep across the recommender and the apply
 * hook (which reads `platform` straight off the row instead of re-deriving it).
 */
interface ChannelConfig {
  displayName: string;
  /** null for a channel with no CustomAttributionModel channel (not registered below). */
  adChannel: AdChannelValue | null;
  /** paid-media API platform slug for budget writes; null = read-only (no writes). */
  platform: SupportedPlatform | null;
  /**
   * Whether the channel carries real adset/ad-group-level budget + revenue.
   * Meta/TikTok do (CBO campaigns aggregate revenue from their adsets); Google/
   * YouTube are campaign-grain only, so aggregating their (0-revenue) child adsets
   * would zero the campaign. Gates all adset-level logic — kept separate from
   * applyability because Google is writable (campaign budgets) yet campaign-grain.
   */
  hasAdsetData: boolean;
}

const META_CHANNEL: ChannelConfig = {
  displayName: 'Meta',
  adChannel: AD_CHANNEL.META,
  platform: 'meta',
  hasAdsetData: true,
};
const TIKTOK_CHANNEL: ChannelConfig = {
  displayName: 'TikTok',
  adChannel: AD_CHANNEL.TIKTOK,
  platform: 'tiktok',
  hasAdsetData: true,
};
const PINTEREST_CHANNEL: ChannelConfig = {
  displayName: 'Pinterest',
  adChannel: AD_CHANNEL.PINTEREST,
  // No Pinterest write integration in the paid-media service yet, so rows are
  // read-only: budgets come from Cube budget_agg. pipeline-dbt carries daily
  // budgets on ad groups (campaign-level spend caps are not modeled) and there
  // is no live budget-level feed, so every Pinterest campaign takes the ABO
  // path and its ad groups surface individually.
  platform: null,
  hasAdsetData: true,
};
const GOOGLE_CHANNEL: ChannelConfig = {
  displayName: 'Google Ads',
  adChannel: AD_CHANNEL.GOOGLE_ADS,
  platform: 'google',
  hasAdsetData: false,
};
const YOUTUBE_CHANNEL: ChannelConfig = {
  displayName: 'YouTube',
  adChannel: AD_CHANNEL.YOUTUBE,
  // A "YouTube" row IS a Google Ads campaign: pipeline-dbt splits each Google
  // campaign into a youtube_ads slice + google_ads residual under the SAME
  // campaign id, and this module re-merges them (campaignDaily keys by campaign
  // id alone), so the row's metrics are campaign totals and its id is writable
  // through the Google adapter like any other Google campaign.
  platform: 'google',
  hasAdsetData: false,
};

/** Cube `media_channel` value (lowercased) → channel config. */
const CHANNEL_BY_CUBE_CHANNEL: Record<string, ChannelConfig> = {
  meta: META_CHANNEL,
  facebook: META_CHANNEL,
  instagram: META_CHANNEL,
  tiktok: TIKTOK_CHANNEL,
  pinterest: PINTEREST_CHANNEL,
  google_ads: GOOGLE_CHANNEL,
  youtube: YOUTUBE_CHANNEL,
  youtube_ads: YOUTUBE_CHANNEL,
};

/** Resolve a Cube media_channel to its config, with derived `canApplyBudget`. */
function mapChannel(cubeChannel: string): ChannelConfig & { canApplyBudget: boolean } {
  // An unregistered channel has no model channel: it must not pick up another
  // channel's models in findModel's channel tiers, only the initiative-type tier.
  const config = CHANNEL_BY_CUBE_CHANNEL[(cubeChannel || '').toLowerCase()] ?? {
    displayName: cubeChannel || 'Unknown',
    adChannel: null,
    platform: null,
    hasAdsetData: false,
  };
  // Applyability is exactly "is there an API platform to write budgets to?"
  return { ...config, canApplyBudget: config.platform !== null };
}

/** Find the best matching attribution model using 3-tier matching:
 *  1. 4-field (initiativeType + adChannel + objective + optimizationGoal)
 *  2. 2-field (initiativeType + adChannel)
 *  3. 1-field (initiativeType only)
 */
export function findModel<M extends AttributionModelLike>(
  models: ReadonlyArray<M>,
  initiativeType: string,
  adChannel: string | null,
  objective?: string | null,
  optimizationGoal?: string | null,
): M | undefined {
  // Tier 1: 4-field match (only when the row has objective/optimizationGoal)
  if (objective || optimizationGoal) {
    const exact4 = models.find(
      (m) =>
        String(m.initiativeType) === String(initiativeType) &&
        m.adChannel === adChannel &&
        (!objective || String(m.adChannelObjective) === String(objective)) &&
        (!optimizationGoal || String(m.adChannelOptimizationGoal) === String(optimizationGoal)),
    );
    if (exact4) return exact4;
  }
  // Tier 2: 2-field match (initiativeType + adChannel)
  const exact2 = models.find((m) => String(m.initiativeType) === String(initiativeType) && m.adChannel === adChannel);
  if (exact2) return exact2;
  // Tier 3: 1-field match (initiativeType only)
  return models.find((m) => String(m.initiativeType) === String(initiativeType));
}

// Revenue-related measures needed for the modeled_revenue calculation.
// Short names (no cube prefix) — these are also the keys of the per-day
// measure records and what calculateModeledRevenueForRow expects.
// days_with_spend is intentionally absent: it is derived per analysis window
// as the count of days with spend > 0.
export const REVENUE_MEASURE_NAMES = [
  'spend',
  'modeled_revenue',
  'brand_value_agg',
  'budget_agg',
  'purchases',
  'ga4_total_revenue',
  'purchase_value_1d_view',
  'purchase_value_1d_engaged_view',
  'revenue_from_search',
  'd30_brand_value',
  'd60_brand_value',
  'd90_brand_value',
  // Shopify
  'shopify_first_touch_revenue',
  'shopify_last_touch_revenue',
  // Meta incrementality
  'purchase_value_incrementality',
  // Triple Whale (standard)
  'triple_whale_linear_total_revenue',
  'triple_whale_last_platform_click_total_revenue',
  'triple_whale_full_first_click_total_revenue',
  'triple_whale_full_last_click_total_revenue',
  'triple_whale_linear_all_total_revenue',
  // Triple Whale (new customer)
  'triple_whale_linear_nc_total_revenue',
  'triple_whale_last_platform_click_nc_total_revenue',
  'triple_whale_full_first_click_nc_total_revenue',
  'triple_whale_full_last_click_nc_total_revenue',
  'triple_whale_linear_all_nc_total_revenue',
];

/** Cube query for the newest data day of campaigns and ad sets. */
export const MAX_DATE_QUERY = {
  dimensions: [
    `${LAST_UPDATED}.brand_media_paid_optimization_campaigns_max_date`,
    `${LAST_UPDATED}.brand_media_paid_optimization_adsets_max_date`,
  ],
  limit: 1,
};

/** Daily-grain campaign query over the fetch range. */
export function campaignCubeQuery(companyId: string, fetchDateRange: [string, string] | null) {
  return {
    dimensions: [
      `${CUBE_CAMPAIGNS}.campaign_id`,
      `${CUBE_CAMPAIGNS}.campaign_name`,
      `${CUBE_CAMPAIGNS}.media_channel`,
      // Delivery status as of each day; the newest day's value is the
      // fallback when the live campaign read carries none.
      `${CUBE_CAMPAIGNS}.campaign_status`,
    ],
    measures: REVENUE_MEASURE_NAMES.map((m) => `${CUBE_CAMPAIGNS}.${m}`),
    filters: [
      {
        member: `${CUBE_CAMPAIGNS}.companyid`,
        operator: 'equals' as const,
        values: [companyId],
      },
    ],
    timeDimensions: [
      {
        dimension: `${CUBE_CAMPAIGNS}.md_date`,
        granularity: 'day' as const,
        dateRange: fetchDateRange ?? undefined,
      },
    ],
    limit: CUBE_ROW_LIMIT,
  };
}

/** Daily-grain ad set query over the fetch range. */
export function adsetCubeQuery(companyId: string, fetchDateRange: [string, string] | null) {
  return {
    dimensions: [
      `${CUBE_ADSETS}.adset_id`,
      `${CUBE_ADSETS}.adset_name`,
      `${CUBE_ADSETS}.campaign_id`,
      `${CUBE_ADSETS}.media_channel`,
      `${CUBE_ADSETS}.objective`,
      `${CUBE_ADSETS}.optimization_goal`,
      // Delivery status as of each day — the only status source for ad set
      // rows (no live ad set read), plus the parent campaign's.
      `${CUBE_ADSETS}.adset_status`,
      `${CUBE_ADSETS}.campaign_status`,
    ],
    measures: REVENUE_MEASURE_NAMES.map((m) => `${CUBE_ADSETS}.${m}`),
    filters: [
      {
        member: `${CUBE_ADSETS}.companyid`,
        operator: 'equals' as const,
        values: [companyId],
      },
    ],
    timeDimensions: [
      {
        dimension: `${CUBE_ADSETS}.md_date`,
        granularity: 'day' as const,
        dateRange: fetchDateRange ?? undefined,
      },
    ],
    limit: CUBE_ROW_LIMIT,
  };
}

export interface DailyMeasures {
  /** YYYY-MM-DD */
  date: string;
  /** Short measure name → value for that day */
  measures: Record<string, number>;
}

export interface WindowAggregate {
  /** Short measure name → value summed over the window (budget_agg via max) */
  measures: Record<string, number>;
  /** Count of days in the window with spend > 0 */
  daysWithSpend: number;
}

/** Add delta days (may be negative) to a YYYY-MM-DD date */
export function addDays(date: string, delta: number): string {
  const [y, m, d] = date.split('-').map(Number);
  // Date.UTC avoids local-timezone shift when serializing via toISOString
  return new Date(Date.UTC(y, m - 1, d + delta)).toISOString().split('T')[0];
}

/** Inclusive window start so that [start, windowEnd] spans periodDays days */
export function windowStartFor(windowEnd: string, periodDays: number): string {
  return addDays(windowEnd, -(periodDays - 1));
}

export interface DateAnchors {
  /** Daily-grain fetch window; ends at n so brand value freshness is observable */
  fetchDateRange: [string, string];
  /** Fixed 7-day window ending at n-1 for the page-level MER metric */
  dateRange7: [string, string];
  /** n-1: the last complete data day */
  latestCompleteDate: string;
  /** The freshness row was missing, so the anchor assumes n = today */
  freshnessUnavailable: boolean;
}

/**
 * Date ranges anchored to n, the newest day with any data (the earlier of the
 * campaign and ad set max dates). The fetch ends at n — NOT the last complete
 * day — so the YouTube brand value anchor can observe whether brand value
 * landed for the newest day, and starts with lag buffer headroom so anchored
 * windows can still span a full period. Every aggregation clips to its own
 * window end, so the partial newest day never reaches a recommendation.
 */
export function resolveDateAnchors(campaignMaxDate: unknown, adsetMaxDate: unknown, today: string): DateAnchors {
  // Normalize to YYYY-MM-DD (Cube may return full ISO datetime strings)
  const dates = [String(campaignMaxDate ?? ''), String(adsetMaxDate ?? '')].filter((d) => d.length > 0).map((s) => s.split('T')[0]);

  // Freshness fallback: the max_dates_by_company row can be missing or
  // carry NULL paid-optimization dates while the campaign/adset data
  // tables are fine (the pipeline rebuilds the freshness table from an
  // inner join over estimator tables that can transiently come up empty,
  // e.g. after a connector outage). Anchoring must not blank the result in
  // that case — assume the pipeline's normal shape (newest day = today,
  // likely partial) and flag it.
  const freshnessUnavailable = dates.length === 0;
  dates.sort();
  // Use the min of both so campaign + adset queries cover the same period
  const newest = freshnessUnavailable ? today : dates[0];
  // n-1: the newest day is likely partial, so the last *complete* day is
  // the day before it
  const latestCompleteDate = addDays(newest, -1);
  return {
    fetchDateRange: [addDays(newest, -(FETCH_WINDOW_DAYS + BRAND_VALUE_LAG_BUFFER_DAYS)), newest],
    dateRange7: [addDays(newest, -7), latestCompleteDate],
    latestCompleteDate,
    freshnessUnavailable,
  };
}

/** Aggregate daily measures over [windowStart, windowEnd] inclusive. All
 *  measures are additive except budget_agg (max-over-days = max-over-window).
 *  daysWithSpend counts distinct DATES with spend > 0 — an entity can carry
 *  several entries per date (the pipeline splits a Google campaign's metrics
 *  into a youtube_ads portion and a google_ads residual under the same
 *  campaign id), and counting entries would double-count those days and
 *  halve avgDailySpend. The end bound matters: the fetch range extends one
 *  day past the last complete day (so brand value freshness can be observed
 *  on the newest, partial day), and Google-platform Demand Gen rows use an
 *  earlier end than other rows. */
export function aggregateWindow(days: DailyMeasures[], windowStart: string, windowEnd: string): WindowAggregate {
  const measures: Record<string, number> = {};
  const spendDates = new Set<string>();
  for (const day of days) {
    if (day.date < windowStart || day.date > windowEnd) continue;
    if ((day.measures.spend || 0) > 0) spendDates.add(day.date);
    for (const [key, value] of Object.entries(day.measures)) {
      if (key === 'budget_agg') {
        measures[key] = Math.max(measures[key] ?? 0, value);
      } else {
        measures[key] = (measures[key] ?? 0) + value;
      }
    }
  }
  return { measures, daysWithSpend: spendDates.size };
}

/** Measures produced by the brand value pipeline (for YouTube: Analytics
 *  engagements × model rates). An attribution model may weight any subset of
 *  the windowed variants with zero weight on brand_value_agg, so a day
 *  "has brand value data" when any of them is non-zero. */
const BRAND_VALUE_MEASURE_NAMES = ['brand_value_agg', 'd30_brand_value', 'd60_brand_value', 'd90_brand_value'];

/** Latest date across the given day series with non-zero brand value, or null
 *  when none of the days carry any. */
export function latestBrandValueDate(seriesList: DailyMeasures[][]): string | null {
  let latest: string | null = null;
  for (const days of seriesList) {
    for (const day of days) {
      if (latest !== null && day.date <= latest) continue;
      if (BRAND_VALUE_MEASURE_NAMES.some((m) => (day.measures[m] || 0) > 0)) {
        latest = day.date;
      }
    }
  }
  return latest;
}

/**
 * Effective current budget for display and % Change. A row can carry no budget
 * (connector not set up, shared Google budget, unregistered channel) and a
 * campaign/ad set can't truly run on $0, so fall back to average daily spend
 * whenever the platform budget is missing or zero. This keeps the Current
 * Budget column meaningful and the % Change a real delta instead of 0%.
 */
export function normalizeCurrentBudget(currentBudget: number, avgDailySpend: number): number {
  return currentBudget > 0 ? currentBudget : avgDailySpend;
}

export interface BaseRowsInput {
  inputs: BudgetInputs;
  /** Daily-grain pivot rows of campaignCubeQuery, keyed by full member name */
  campaignRows: ReadonlyArray<Record<string, unknown>>;
  /** Daily-grain pivot rows of adsetCubeQuery, keyed by full member name */
  adsetRows: ReadonlyArray<Record<string, unknown>>;
  mediaTargets: ReadonlyArray<MediaTargetLike>;
  /** Live Meta campaigns ([] when Meta is not connected) */
  metaCampaigns: ReadonlyArray<LiveCampaignLike>;
  tiktokCampaigns?: ReadonlyArray<LiveCampaignLike>;
  googleCampaigns?: ReadonlyArray<LiveCampaignLike>;
  models: ReadonlyArray<AttributionModelLike>;
  customTypes?: ReadonlyArray<CustomTypeCategoryLike>;
  /** The recent change window and its audited changes */
  recentChanges: { days: number; holds: ReadonlyArray<RecentChangeHoldLike> };
  /** entityId → analysis period days; DEFAULT_ANALYSIS_PERIOD otherwise */
  periodByEntity?: ReadonlyMap<string, number>;
  latestCompleteDate: string;
  /** First day of the daily fetch (resolveDateAnchors().fetchDateRange[0]) */
  fetchStart: string | null;
  /** Clock for "days ago"; injectable for tests */
  nowMs: number;
}

const EMPTY_PERIODS: ReadonlyMap<string, number> = new Map();

/**
 * Everything up to (not including) the scaling pass: raw recommendations,
 * activity, delivery and labels. Rows leave with adjustedBudget = the raw
 * recommendation and shouldPause = false; allocateBudgets scales them.
 */
export function buildBaseRows(input: BaseRowsInput): RecommendationRow[] {
  const {
    inputs,
    campaignRows: campaignData,
    adsetRows: adsetData,
    mediaTargets,
    metaCampaigns: metaCampaignsData,
    tiktokCampaigns,
    googleCampaigns,
    models: modelsArray,
    customTypes,
    recentChanges: holdsData,
    latestCompleteDate,
    nowMs,
  } = input;
  const periodByEntity = input.periodByEntity ?? EMPTY_PERIODS;
  const getCategory = (type: string) => categoryFor(type, customTypes);

  // Build initiative type lookup from media targets.
  // Adset-level targets are keyed by adset; campaign-level targets (ad_set_id
  // empty) are keyed by campaign.
  const targetLookup = new Map<string, string>();
  for (const t of mediaTargets) {
    if (t.ad_set_id && t.ad_set_id !== '') {
      targetLookup.set(`adset_${t.ad_set_id}`, t.initiativeType);
    } else if (t.campaign_id) {
      targetLookup.set(`campaign_${t.campaign_id}`, t.initiativeType);
    }
  }

  // An ad set's initiative type: its own ad-set-level target, else its
  // campaign's campaign-level target. This is how attribution resolves it
  // (pipeline-dbt prep__attribution__derived__adset_initiative_types: the
  // ad set's own target at priority 1, the campaign-level target fanned out
  // to every ad set / ad group of the campaign at priority 2, for Meta,
  // TikTok and Google alike), so an ad set counts as untyped here only when
  // attribution leaves its spend untyped too. Google's campaign-keyed
  // residual row (adset_id = campaign_id) resolves the same way.
  const adsetTypeOf = (adsetId: string, campaignId: string): string | null =>
    targetLookup.get(`adset_${adsetId}`) ?? targetLookup.get(`campaign_${campaignId}`) ?? null;

  // Build campaign budget level and budget lookups from live platform data
  const campaignBudgetLevel = new Map<string, string>();
  const campaignBudgets = new Map<string, number>();

  for (const c of metaCampaignsData) {
    campaignBudgetLevel.set(c.id, c.budget_level ?? 'ADSET');
    const budget = c.daily_budget ? parseInt(c.daily_budget, 10) / 100 : 0;
    campaignBudgets.set(c.id, budget);
  }

  if (tiktokCampaigns) {
    for (const c of tiktokCampaigns) {
      campaignBudgetLevel.set(c.id, c.budget_level ?? 'ADSET');
      const budget = c.daily_budget ? parseInt(c.daily_budget, 10) / 100 : 0;
      campaignBudgets.set(c.id, budget);
    }
  }

  const googleBiddingByCampaign = new Map<string, ReturnType<typeof deriveGoogleBidding>>();
  if (googleCampaigns) {
    for (const c of googleCampaigns) {
      // Google Ads budgets live on the campaign (shared "campaign budget"
      // resource), never on ad groups, so every Google campaign is CAMPAIGN-level.
      campaignBudgetLevel.set(c.id, 'CAMPAIGN');
      // The google_ads adapter returns `dailyBudgetCents` (camelCase number),
      // not the Meta-shaped `daily_budget` cents-string. Read both so this keeps
      // working if the adapter is later aligned to the shared Campaign contract.
      const cents = (c as { dailyBudgetCents?: number }).dailyBudgetCents ?? (c.daily_budget ? parseInt(c.daily_budget, 10) : 0);
      campaignBudgets.set(c.id, cents / 100);
      // Bidding strategy + Google's constraint diagnosis (budget- vs
      // target-limited) — what the smart-scale approval flow keys off.
      googleBiddingByCampaign.set(c.id, deriveGoogleBidding(c));
    }
  }

  // Live delivery status per campaign, preferred over the (daily) Cube
  // status when the platform read carries one. Meta: effective_status folds
  // the parent/account state in. TikTok: raw CAMPAIGN_STATUS_* under
  // campaign_id (or id). Google: our ACTIVE/PAUSED mapping, and isLive=false
  // (ENDED etc.) is inactive regardless.
  const liveCampaignStatus = new Map<string, PlatformStatus>();
  const recordLiveStatus = (id: unknown, raw: unknown) => {
    const key = id === undefined || id === null ? '' : String(id);
    if (!key) return;
    const status = normalizePlatformStatus(raw);
    if (status) liveCampaignStatus.set(key, status);
  };
  for (const c of metaCampaignsData) {
    recordLiveStatus(c.id, c.effective_status ?? c.status);
  }
  if (tiktokCampaigns) {
    for (const c of tiktokCampaigns) {
      const raw = c as { campaign_id?: unknown; status?: unknown };
      recordLiveStatus(raw.campaign_id ?? c.id, raw.status);
    }
  }
  if (googleCampaigns) {
    for (const c of googleCampaigns) {
      const raw = c as { isLive?: unknown; status?: unknown };
      recordLiveStatus(c.id, raw.isLive === false ? 'PAUSED' : raw.status);
    }
  }

  // === Parse daily-grain pivot rows into per-entity day series ===
  const readDay = (row: Record<string, unknown>, cube: string): string =>
    String(row[`${cube}.md_date.day`] ?? row[`${cube}.md_date`] ?? '').split('T')[0];

  const readMeasures = (row: Record<string, unknown>, cube: string): Record<string, number> => {
    const measures: Record<string, number> = {};
    for (const m of REVENUE_MEASURE_NAMES) {
      measures[m] = Number(row[`${cube}.${m}`]) || 0;
    }
    return measures;
  };

  interface CampaignMeta {
    name: string;
    cubeChannel: string;
    /** Cube campaign_status on the newest day that reported one. */
    cubeStatus: string | null;
    cubeStatusDate: string;
  }
  const campaignMeta = new Map<string, CampaignMeta>();
  const campaignDaily = new Map<string, DailyMeasures[]>();

  for (const row of campaignData) {
    const campaignId = String(row[`${CUBE_CAMPAIGNS}.campaign_id`] ?? '');
    if (!campaignId) continue;
    const date = readDay(row, CUBE_CAMPAIGNS);
    if (!date) continue;

    const status = String(row[`${CUBE_CAMPAIGNS}.campaign_status`] ?? '');
    const existing = campaignMeta.get(campaignId);
    if (!existing) {
      campaignMeta.set(campaignId, {
        name: String(row[`${CUBE_CAMPAIGNS}.campaign_name`] ?? 'Unknown'),
        cubeChannel: String(row[`${CUBE_CAMPAIGNS}.media_channel`] ?? ''),
        cubeStatus: status || null,
        cubeStatusDate: date,
      });
    } else if (status && date > existing.cubeStatusDate) {
      existing.cubeStatus = status;
      existing.cubeStatusDate = date;
    }
    let days = campaignDaily.get(campaignId);
    if (!days) {
      days = [];
      campaignDaily.set(campaignId, days);
    }
    days.push({ date, measures: readMeasures(row, CUBE_CAMPAIGNS) });
  }

  interface AdsetMeta {
    name: string;
    campaignId: string;
    cubeChannel: string;
    objective: string | null;
    optimizationGoal: string | null;
    /** Cube adset_status / campaign_status on the newest day that reported them. */
    cubeStatus: string | null;
    cubeCampaignStatus: string | null;
    cubeStatusDate: string;
  }
  const adsetMeta = new Map<string, AdsetMeta>();
  const adsetDaily = new Map<string, DailyMeasures[]>();
  const adsetsByCampaign = new Map<string, string[]>();

  for (const row of adsetData) {
    const adsetId = String(row[`${CUBE_ADSETS}.adset_id`] ?? '');
    const campaignId = String(row[`${CUBE_ADSETS}.campaign_id`] ?? '');
    if (!adsetId || !campaignId) continue;
    const date = readDay(row, CUBE_ADSETS);
    if (!date) continue;

    const status = String(row[`${CUBE_ADSETS}.adset_status`] ?? '');
    const campaignStatus = String(row[`${CUBE_ADSETS}.campaign_status`] ?? '');
    const existing = adsetMeta.get(adsetId);
    if (!existing) {
      adsetMeta.set(adsetId, {
        name: String(row[`${CUBE_ADSETS}.adset_name`] ?? 'Unknown'),
        campaignId,
        cubeChannel: String(row[`${CUBE_ADSETS}.media_channel`] ?? ''),
        objective: String(row[`${CUBE_ADSETS}.objective`] ?? '') || null,
        optimizationGoal: String(row[`${CUBE_ADSETS}.optimization_goal`] ?? '') || null,
        cubeStatus: status || null,
        cubeCampaignStatus: campaignStatus || null,
        cubeStatusDate: date,
      });
      let siblings = adsetsByCampaign.get(campaignId);
      if (!siblings) {
        siblings = [];
        adsetsByCampaign.set(campaignId, siblings);
      }
      siblings.push(adsetId);
    } else if ((status || campaignStatus) && date > existing.cubeStatusDate) {
      if (status) existing.cubeStatus = status;
      if (campaignStatus) existing.cubeCampaignStatus = campaignStatus;
      existing.cubeStatusDate = date;
    }
    let days = adsetDaily.get(adsetId);
    if (!days) {
      days = [];
      adsetDaily.set(adsetId, days);
    }
    days.push({ date, measures: readMeasures(row, CUBE_ADSETS) });
  }

  // === YouTube Demand Gen analysis-window anchor ===
  // YouTube campaign metrics join fresh Google Ads spend with YouTube
  // Analytics brand value that lands a few days later, so the trailing days
  // of the fetch have spend > 0 but brand value 0. A window anchored to
  // spend freshness deflates Brand ROAS for Demand Gen campaigns — anchor
  // their windows to n-1 where n is the latest day with non-zero brand
  // value across those campaigns instead. Channel-level (not per-campaign)
  // so a long-paused campaign can't drag its own window into the distant
  // past.
  //
  // Scope covers BOTH Google-platform channels: pipeline-dbt splits every
  // Google campaign into a YouTube-attributed portion (media_channel
  // youtube_ads) and a residual (google_ads) under the SAME campaign id
  // (prep__google_ads__metrics_final subtracts the video report from
  // campaign totals), so a YouTube Demand Gen campaign can surface here
  // under either channel — whichever daily row is parsed first. Its brand
  // value comes from the lagged YouTube Analytics join either way; the
  // google_ads residual rows carry none (brand value rates exist only for
  // social-engagement metrics).
  const isBrandType = (type: string | undefined): boolean => !!type && getCategory(type) === 'BRAND';
  const isGoogleChannel = (adChannel: string | null): boolean => adChannel === AD_CHANNEL.YOUTUBE || adChannel === AD_CHANNEL.GOOGLE_ADS;

  const googleDemandGenCampaignIds = new Set<string>();
  for (const [campaignId, meta] of campaignMeta) {
    if (!isGoogleChannel(mapChannel(meta.cubeChannel).adChannel)) {
      continue;
    }
    const campaignType = targetLookup.get(`campaign_${campaignId}`);
    const isDemandGen = campaignType
      ? isBrandType(campaignType)
      : (adsetsByCampaign.get(campaignId) ?? []).some((adsetId) => isBrandType(targetLookup.get(`adset_${adsetId}`)));
    if (isDemandGen) googleDemandGenCampaignIds.add(campaignId);
  }

  const latestGoogleDgBrandValue = latestBrandValueDate([...googleDemandGenCampaignIds].map((id) => campaignDaily.get(id) ?? []));
  // n-1 of the latest brand value day (the newest day is likely partial),
  // clamped to the standard complete date. Null — no brand value anywhere
  // in the fetch window — falls back to the standard window: with the
  // pipeline that stale there is no complete period to anchor to anyway.
  let googleDgDataThrough: string | null = null;
  if (latestGoogleDgBrandValue) {
    const end = addDays(latestGoogleDgBrandValue, -1);
    googleDgDataThrough = end < latestCompleteDate ? end : latestCompleteDate;
  }

  // === Per-adset windowed aggregation + modeled values ===
  // Uses adset-level objective/optimization_goal for proper model matching.
  // Cached per (adsetId, window) since a CBO campaign's window drives
  // its children and several campaigns may share the same window.
  interface AdsetComputed {
    spend: number;
    budget: number;
    daysWithSpend: number;
    measures: Record<string, number>;
    modeledRevenue: number;
    brandValue: number;
  }
  const adsetComputedCache = new Map<string, AdsetComputed>();

  function computeAdsetModeled(adsetId: string, windowStart: string, windowEnd: string): AdsetComputed {
    const cacheKey = `${adsetId}|${windowStart}|${windowEnd}`;
    const cached = adsetComputedCache.get(cacheKey);
    if (cached) return cached;

    const meta = adsetMeta.get(adsetId)!;
    const { measures, daysWithSpend } = aggregateWindow(adsetDaily.get(adsetId) ?? [], windowStart, windowEnd);
    const spend = measures.spend || 0;
    const type = adsetTypeOf(adsetId, meta.campaignId);

    let modeledRevenue: number;
    let brandValue: number;

    if (type) {
      const { adChannel } = mapChannel(meta.cubeChannel);
      const model = findModel(modelsArray, type, adChannel, meta.objective, meta.optimizationGoal);
      const frontendMR = calculateModeledRevenueForRow({
        rowData: measures,
        model,
        adChannel,
      });
      const cubeMR = measures.modeled_revenue || 0;
      modeledRevenue = frontendMR > 0 ? frontendMR : cubeMR;

      const bvBreakdown = calculateBrandValueBreakdownForRow({
        rowData: measures,
        model,
      });
      brandValue = bvBreakdown.total > 0 ? bvBreakdown.total : measures.brand_value_agg || 0;
    } else {
      modeledRevenue = measures.modeled_revenue || 0;
      brandValue = measures.brand_value_agg || 0;
    }

    const computed: AdsetComputed = {
      spend,
      budget: measures.budget_agg || 0,
      daysWithSpend,
      measures,
      modeledRevenue,
      brandValue,
    };
    adsetComputedCache.set(cacheKey, computed);
    return computed;
  }

  const aov = inputs.avgOrderValue;
  // Confidence is driven by a row's estimated conversions. Demand Capture (DR)
  // entities estimate conversions from modeled revenue; Demand Generation
  // (BRAND) entities are measured on the purchases their brand value drives, so
  // they estimate from modeled brand value instead. Unassigned rows (no
  // category) fall back to modeled revenue.
  const conversionsFor = (modeledRevenue: number, modeledBrandValue: number, category: EntityCategory | null): number | null => {
    if (aov <= 0) return null;
    const basis = category === 'BRAND' ? modeledBrandValue : modeledRevenue;
    return basis / aov;
  };

  // Estimated conversions over EVERY candidate analysis period, so a caller
  // can auto-select the shortest window that reaches High confidence
  // without recomputing per click. Each candidate window ends at the row's
  // own dataThroughDate (same anchor rules as its live window), and
  // `modeledForWindow` is the row's own modeled-value path, so the entry
  // for the current period equals the row's estimatedConversions.
  const conversionsByPeriod = (
    dataThroughDate: string,
    category: EntityCategory | null,
    modeledForWindow: (windowStart: string, windowEnd: string) => { modeledRevenue: number; modeledBrandValue: number },
  ): Record<number, number | null> => {
    const byPeriod: Record<number, number | null> = {};
    for (const candidate of ANALYSIS_PERIOD_OPTIONS) {
      if (aov <= 0) {
        byPeriod[candidate] = null;
        continue;
      }
      const { modeledRevenue, modeledBrandValue } = modeledForWindow(windowStartFor(dataThroughDate, candidate), dataThroughDate);
      byPeriod[candidate] = conversionsFor(modeledRevenue, modeledBrandValue, category);
    }
    return byPeriod;
  };

  const rows: RecommendationRow[] = [];

  // === Activity, delivery and recent-change labels ===
  // The recent change window (Financial Settings; 0 = labels off).
  const windowDays = holdsData.days;
  const fetchStart = input.fetchStart;

  // Audited recent changes (budget / bid target / enable), newest per
  // entity. Platform is part of the identity: Meta and Google ids are both
  // numeric and can collide. A change recorded without a platform is
  // matched by type + id alone as a fallback.
  const auditedChangeByKey = new Map<string, RecentChange>();
  for (const hold of holdsData.holds) {
    const atMs = Date.parse(hold.at);
    auditedChangeByKey.set(`${hold.platform ?? ''}:${hold.entityType}:${hold.entityId}`, {
      reason: hold.reason,
      at: hold.at,
      daysAgo: Number.isFinite(atMs) ? Math.max(0, Math.floor((nowMs - atMs) / 86_400_000)) : 0,
      auditId: hold.auditId,
      actor: hold.actor,
      ...(hold.previousDailyBudgetCents !== undefined ? { previousDailyBudget: hold.previousDailyBudgetCents / 100 } : {}),
      ...(hold.newDailyBudgetCents !== undefined ? { newDailyBudget: hold.newDailyBudgetCents / 100 } : {}),
    });
  }

  // "Recently created" is read from the data, not the platform: the first
  // day with spend inside the fetch window. A first spend ON the window's
  // first day proves nothing (it may have spent before the window), so
  // only a later first day counts. An entity that resumed after a long
  // pause reads as new too — a restart deserves the same care.
  const firstSpendChange = (days: DailyMeasures[]): RecentChange | null => {
    if (windowDays <= 0 || !fetchStart) return null;
    let first: string | null = null;
    for (const day of days) {
      if ((day.measures.spend || 0) > 0 && (first === null || day.date < first)) {
        first = day.date;
      }
    }
    if (first === null || first <= fetchStart) return null;
    const daysAgo = daysSince(first, nowMs);
    if (daysAgo === null || daysAgo > windowDays) return null;
    return {
      reason: 'first-spend',
      at: first,
      daysAgo,
      auditId: null,
      actor: null,
    };
  };

  const recentChangesFor = (
    platform: SupportedPlatform | null,
    entityType: 'campaign' | 'adset',
    entityId: string,
    days: DailyMeasures[],
  ): RecentChange[] | undefined => {
    if (windowDays <= 0) return undefined;
    const changes: RecentChange[] = [];
    const audited =
      auditedChangeByKey.get(`${platform ?? ''}:${entityType}:${entityId}`) ?? auditedChangeByKey.get(`:${entityType}:${entityId}`);
    if (audited) changes.push(audited);
    const created = firstSpendChange(days);
    if (created) changes.push(created);
    if (changes.length === 0) return undefined;
    return changes.sort((a, b) => a.daysAgo - b.daysAgo);
  };

  // Delivery status + "in the pool" + spend-to-budget for a row. Status
  // prefers the live campaign read, then the newest Cube status; an ad set
  // under a paused campaign is paused. Unknown status fails OPEN to the
  // spend test, so a value we do not recognize never hides a live row.
  const activityFor = (args: {
    campaignId: string;
    adsetId?: string;
    periodSpend: number;
    /** The platform's own daily budget ($); 0 when it reports none. */
    rawBudget: number;
    periodDays: number;
  }): Pick<RecommendationRow, 'platformStatus' | 'isActive' | 'inactiveReason' | 'hasPlatformBudget' | 'deliveryRatio'> => {
    const adset = args.adsetId ? adsetMeta.get(args.adsetId) : undefined;
    const campaignStatus =
      liveCampaignStatus.get(args.campaignId) ??
      normalizePlatformStatus(adset?.cubeCampaignStatus ?? campaignMeta.get(args.campaignId)?.cubeStatus);
    const ownStatus = adset ? normalizePlatformStatus(adset.cubeStatus) : campaignStatus;
    const platformStatus: PlatformStatus | null =
      ownStatus === 'INACTIVE' || campaignStatus === 'INACTIVE' ? 'INACTIVE' : (ownStatus ?? campaignStatus);
    const paused = platformStatus === 'INACTIVE';
    const isActive = !paused && args.periodSpend > 0;
    return {
      ...(platformStatus ? { platformStatus } : {}),
      isActive,
      ...(isActive ? {} : { inactiveReason: paused ? 'paused' : 'no-spend' }),
      hasPlatformBudget: args.rawBudget > 0,
      deliveryRatio: deliveryRatioFor(args.periodSpend, args.periodDays, args.rawBudget),
    };
  };

  // Helper: compute ROAS and recommendation for an entity from its
  // window-aggregated measures.
  // When overrides are provided (from adset-level aggregation), use them
  // instead of computing from the aggregated measures — this ensures proper
  // model matching using adset-level objective/optimization_goal.
  // daysWithSpend always comes from the entity's own daily series (cannot
  // sum adset-level values as overlapping days would be overcounted).
  function computeRecommendation(
    agg: WindowAggregate,
    period: number,
    initiativeType: string,
    category: EntityCategory,
    currentBudget: number,
    adChannel: string | null,
    overrides?: {
      modeledRevenue?: number;
      brandValue?: number;
      spend?: number;
    },
  ) {
    const spend = overrides?.spend ?? (agg.measures.spend || 0);
    const activeDays = agg.daysWithSpend || period;

    let modeledRevenue: number;
    let modeledBrandValue: number;

    if (overrides?.modeledRevenue !== undefined && overrides?.brandValue !== undefined) {
      modeledRevenue = overrides.modeledRevenue;
      modeledBrandValue = overrides.brandValue;
    } else {
      // Calculate modeled_revenue using attribution model weights
      const model = findModel(modelsArray, initiativeType, adChannel);
      const frontendModeledRevenue = calculateModeledRevenueForRow({
        rowData: agg.measures,
        model,
        adChannel,
      });

      // Fall back to Cube modeled_revenue if the weighted calculation returns 0
      const cubeModeledRevenue = agg.measures.modeled_revenue || 0;
      modeledRevenue = frontendModeledRevenue > 0 ? frontendModeledRevenue : cubeModeledRevenue;

      // Compute modeled brand value using attribution model weights
      const brandValueBreakdown = calculateBrandValueBreakdownForRow({
        rowData: agg.measures,
        model,
      });
      modeledBrandValue = brandValueBreakdown.total > 0 ? brandValueBreakdown.total : agg.measures.brand_value_agg || 0;
    }

    // Compute both ROAS for all entities
    const drRoas = spend > 0 ? modeledRevenue / spend : 0;
    const brandRoas = spend > 0 ? modeledBrandValue / spend : 0;

    // The "active" ROAS used for recommendation depends on category
    const actualRoas = category === 'BRAND' ? brandRoas : drRoas;

    const targetRoas = category === 'BRAND' ? inputs.targetBrandRoas : inputs.targetDRRoas;

    const avgDailySpend = spend / activeDays;
    const rawRec = targetRoas > 0 && avgDailySpend > 0 ? (actualRoas / targetRoas) * avgDailySpend : 0;

    const hasMissingData = spend > 0 && modeledRevenue === 0 && category === 'DR';

    return {
      spend,
      drRoas,
      brandRoas,
      rawRec,
      hasMissingData,
      currentBudget,
      avgDailySpend,
      activeDays,
      modeledRevenue,
      modeledBrandValue,
    };
  }

  // === Process campaigns at campaign level ===
  // For Meta/TikTok: only CBO campaigns (budget_level === "CAMPAIGN")
  // For Google/YouTube: always campaign-level (budgets are always at campaign level)
  for (const [campaignId, meta] of campaignMeta) {
    const { displayName, adChannel, platform, canApplyBudget, hasAdsetData } = mapChannel(meta.cubeChannel);

    const level = campaignBudgetLevel.get(campaignId);
    // Channels with adset-level budgets (Meta/TikTok): only surface CBO campaigns
    // here; ABO campaigns are handled in the adset loop below. Campaign-grain
    // channels (Google/YouTube) have no adset budgets, so always show them here.
    if (hasAdsetData && level !== 'CAMPAIGN') continue;

    const period = periodByEntity.get(campaignId) ?? DEFAULT_ANALYSIS_PERIOD;
    // Google-platform Demand Gen windows end where brand value is
    // complete, not where spend is
    const dataThroughDate = googleDemandGenCampaignIds.has(campaignId) && googleDgDataThrough ? googleDgDataThrough : latestCompleteDate;
    const windowStart = windowStartFor(dataThroughDate, period);
    const campaignAgg = aggregateWindow(campaignDaily.get(campaignId) ?? [], windowStart, dataThroughDate);

    // Child adsets aggregated over the campaign's analysis window, each
    // typed the way attribution types it (adsetTypeOf)
    const childAdsetIds = adsetsByCampaign.get(campaignId) ?? [];
    const childAdsets = childAdsetIds.map((adsetId) => ({
      adsetId,
      initiativeType: adsetTypeOf(adsetId, campaignId),
      ...computeAdsetModeled(adsetId, windowStart, dataThroughDate),
    }));

    // Type the campaign by its spending ad sets: the type carrying the most
    // spend wins. An ad set with no target of its own inherits the
    // campaign-level type, so its spend counts under that type, while an ad
    // set's own type outranks the campaign-level one for its own spend. When
    // the two disagree, the campaign takes the type most of its spend is
    // attributed under. Only an ad set with neither is uncategorized.
    const adsetsWithSpend = childAdsets.filter((a) => a.spend > 0);

    let initiativeType: string;
    let hasUncategorizedAdsets = false;
    let category: EntityCategory | null = null;

    if (adsetsWithSpend.length > 0) {
      const categorized = adsetsWithSpend.filter((a) => a.initiativeType !== null);
      const uncategorized = adsetsWithSpend.filter((a) => a.initiativeType === null);

      if (categorized.length === 0) {
        initiativeType = 'UNASSIGNED';
      } else {
        hasUncategorizedAdsets = uncategorized.length > 0;
        const spendByType = new Map<string, number>();
        for (const a of categorized) {
          const prev = spendByType.get(a.initiativeType!) ?? 0;
          spendByType.set(a.initiativeType!, prev + a.spend);
        }
        let maxSpend = 0;
        initiativeType = 'UNASSIGNED';
        for (const [type, spend] of spendByType) {
          if (spend > maxSpend) {
            maxSpend = spend;
            initiativeType = type;
          }
        }
        category = getCategory(initiativeType);
      }
    } else {
      initiativeType = 'UNASSIGNED';
    }

    // No spending ad set to derive it from (none in the window, or no ad
    // set rows at all): fall back to the campaign-level assignment.
    if (category === null) {
      const campaignLevelType = targetLookup.get(`campaign_${campaignId}`);
      if (campaignLevelType) {
        initiativeType = campaignLevelType;
        category = getCategory(campaignLevelType);
      }
    }

    // Applyable channels (Meta/TikTok/Google, incl. YouTube via the Google
    // adapter): use the live API campaign budget. Non-applyable (unregistered)
    // channels: use Cube budget_agg, falling back to max child adset budget
    // (budget data may only exist at adset level in Cube).
    let currentBudget: number;
    if (canApplyBudget) {
      currentBudget = campaignBudgets.get(campaignId) ?? 0;
    } else {
      const campaignBudgetAgg = campaignAgg.measures.budget_agg || 0;
      if (campaignBudgetAgg > 0) {
        currentBudget = campaignBudgetAgg;
      } else {
        // Fallback: read budget from adset-level Cube data
        currentBudget = childAdsets.reduce((max, a) => Math.max(max, a.budget), 0);
      }
    }

    if (category) {
      // `let category` narrows here but not inside the closures below
      const assignedCategory = category;
      // Only aggregate from child ad sets for channels with real adset-level
      // data (Meta/TikTok CBO), where summing per-adset modeled revenue gives
      // accurate per-objective model matching. Google/YouTube replicate
      // campaign data down to adset rows but only carry revenue at the
      // campaign grain, so their child adsets resolve to 0 modeled revenue —
      // using that override would zero out the campaign. Passing `undefined`
      // lets computeRecommendation derive revenue from the campaign-level
      // model + Cube modeled_revenue fallback instead. Parameterized by
      // window (cached per adset+window) so candidate periods reuse it.
      const adsetAggFor = (start: string, end: string) =>
        hasAdsetData && childAdsetIds.length > 0
          ? childAdsetIds.reduce(
              (acc, adsetId) => {
                const a = computeAdsetModeled(adsetId, start, end);
                acc.modeledRevenue += a.modeledRevenue;
                acc.brandValue += a.brandValue;
                acc.spend += a.spend;
                return acc;
              },
              { modeledRevenue: 0, brandValue: 0, spend: 0 },
            )
          : undefined;
      const modeledForWindow = (start: string, end: string) => {
        const r = computeRecommendation(
          aggregateWindow(campaignDaily.get(campaignId) ?? [], start, end),
          period,
          initiativeType,
          assignedCategory,
          currentBudget,
          adChannel,
          adsetAggFor(start, end),
        );
        return {
          modeledRevenue: r.modeledRevenue,
          modeledBrandValue: r.modeledBrandValue,
        };
      };
      const adsetAgg = adsetAggFor(windowStart, dataThroughDate);
      const rec = computeRecommendation(campaignAgg, period, initiativeType, assignedCategory, currentBudget, adChannel, adsetAgg);
      const estimatedConversions = conversionsFor(rec.modeledRevenue, rec.modeledBrandValue, assignedCategory);
      const estimatedConversionsByPeriod = conversionsByPeriod(dataThroughDate, assignedCategory, modeledForWindow);
      rows.push({
        entityId: campaignId,
        entityName: meta.name,
        entityType: 'campaign',
        // Google/YouTube derive revenue from campaignAgg.measures; Meta/TikTok
        // CBO use an adset-summed override no single measures dict reproduces.
        measures: adsetAgg ? undefined : campaignAgg.measures,
        campaignId,
        initiativeType,
        category,
        channel: displayName,
        platform,
        canApplyBudget,
        ...activityFor({
          campaignId,
          periodSpend: rec.spend,
          rawBudget: currentBudget,
          periodDays: period,
        }),
        recentChanges: recentChangesFor(platform, 'campaign', campaignId, campaignDaily.get(campaignId) ?? []),
        periodSpend: rec.spend,
        drRoas: rec.drRoas,
        brandRoas: rec.brandRoas,
        currentDailyBudget: normalizeCurrentBudget(currentBudget, rec.avgDailySpend),
        recommendedBudget: rec.rawRec,
        performanceBudget: rec.rawRec,
        avgDailySpend: rec.avgDailySpend,
        daysWithSpend: rec.activeDays,
        adjustedBudget: rec.rawRec,
        percentChange: 0,
        hasMissingData: rec.hasMissingData,
        modeledRevenuePeriod: rec.modeledRevenue,
        brandValuePeriod: rec.modeledBrandValue,
        shouldPause: false,
        hasUncategorizedAdsets,
        childAdsetIds: hasAdsetData ? childAdsets.map((a) => a.adsetId) : undefined,
        analysisPeriodDays: period,
        dataThroughDate,
        estimatedConversions,
        confidence: confidenceFor(estimatedConversions),
        estimatedConversionsByPeriod,
      });
    } else {
      // Unassigned — show entity with no recommendation
      const spend = campaignAgg.measures.spend || 0;
      const brandValue = campaignAgg.measures.brand_value_agg || 0;
      const modeledRevenue = campaignAgg.measures.modeled_revenue || 0;
      const estimatedConversions = conversionsFor(modeledRevenue, brandValue, null);
      const estimatedConversionsByPeriod = conversionsByPeriod(dataThroughDate, null, (start, end) => {
        const m = aggregateWindow(campaignDaily.get(campaignId) ?? [], start, end).measures;
        return {
          modeledRevenue: m.modeled_revenue || 0,
          modeledBrandValue: m.brand_value_agg || 0,
        };
      });
      rows.push({
        entityId: campaignId,
        entityName: meta.name,
        entityType: 'campaign',
        measures: campaignAgg.measures,
        campaignId,
        initiativeType,
        category: null,
        channel: displayName,
        platform,
        canApplyBudget,
        periodSpend: spend,
        drRoas: spend > 0 ? modeledRevenue / spend : 0,
        brandRoas: spend > 0 ? brandValue / spend : 0,
        ...activityFor({
          campaignId,
          periodSpend: spend,
          rawBudget: currentBudget,
          periodDays: period,
        }),
        recentChanges: recentChangesFor(platform, 'campaign', campaignId, campaignDaily.get(campaignId) ?? []),
        // An unassigned row still spends: it is counted in the pool at its
        // current budget, so give it the same normalized budget and average
        // spend an assigned row would carry.
        currentDailyBudget: normalizeCurrentBudget(currentBudget, campaignAgg.daysWithSpend > 0 ? spend / campaignAgg.daysWithSpend : 0),
        recommendedBudget: 0,
        performanceBudget: 0,
        avgDailySpend: campaignAgg.daysWithSpend > 0 ? spend / campaignAgg.daysWithSpend : 0,
        daysWithSpend: campaignAgg.daysWithSpend,
        adjustedBudget: 0,
        percentChange: 0,
        hasMissingData: false,
        modeledRevenuePeriod: modeledRevenue,
        brandValuePeriod: brandValue,
        shouldPause: false,
        hasUncategorizedAdsets,
        childAdsetIds: hasAdsetData ? childAdsets.map((a) => a.adsetId) : undefined,
        analysisPeriodDays: period,
        dataThroughDate,
        estimatedConversions,
        confidence: confidenceFor(estimatedConversions),
        estimatedConversionsByPeriod,
      });
    }
  }

  // === Process ABO adsets (budget at adset level) ===
  // Only for Meta/TikTok; Google/YouTube are always handled at campaign level above
  for (const [adsetId, meta] of adsetMeta) {
    const { displayName, adChannel, platform, canApplyBudget, hasAdsetData } = mapChannel(meta.cubeChannel);

    // Google/YouTube have no adset-level budgets, so they are handled entirely
    // at campaign level above — skip them here. (Google is applyable but
    // campaign-level only, so gate on hasAdsetData, not canApplyBudget.)
    if (!hasAdsetData) continue;

    const level = campaignBudgetLevel.get(meta.campaignId);
    if (level === 'CAMPAIGN') continue; // CBO — handled at campaign level

    const period = periodByEntity.get(adsetId) ?? DEFAULT_ANALYSIS_PERIOD;
    // Mirror the campaign loop's brand-value anchor via the parent
    // campaign. Unreachable for Google/YouTube today (the hasAdsetData
    // guard above skips them), but keeps the window invariant local if a
    // Google-platform channel ever surfaces ad set rows here.
    const dataThroughDate =
      googleDemandGenCampaignIds.has(meta.campaignId) && googleDgDataThrough ? googleDgDataThrough : latestCompleteDate;
    const windowStart = windowStartFor(dataThroughDate, period);
    const computed = computeAdsetModeled(adsetId, windowStart, dataThroughDate);

    const initiativeType = adsetTypeOf(adsetId, meta.campaignId) ?? 'UNASSIGNED';

    // For Meta/TikTok: use Cube budget_agg (no live adset budget from API)
    const currentBudget = computed.budget;

    // The ad set's own modeled values for any candidate window
    const modeledForAdsetWindow = (start: string, end: string) => {
      const c = computeAdsetModeled(adsetId, start, end);
      return {
        modeledRevenue: c.modeledRevenue,
        modeledBrandValue: c.brandValue,
      };
    };

    if (initiativeType !== 'UNASSIGNED') {
      const category = getCategory(initiativeType);
      const rec = computeRecommendation(
        {
          measures: computed.measures,
          daysWithSpend: computed.daysWithSpend,
        },
        period,
        initiativeType,
        category,
        currentBudget,
        adChannel,
        {
          modeledRevenue: computed.modeledRevenue,
          brandValue: computed.brandValue,
          spend: computed.spend,
        },
      );
      const estimatedConversions = conversionsFor(rec.modeledRevenue, rec.modeledBrandValue, category);
      const estimatedConversionsByPeriod = conversionsByPeriod(dataThroughDate, category, modeledForAdsetWindow);
      rows.push({
        entityId: adsetId,
        entityName: meta.name,
        entityType: 'adset',
        measures: computed.measures,
        campaignId: meta.campaignId,
        initiativeType,
        category,
        channel: displayName,
        platform,
        canApplyBudget,
        ...activityFor({
          campaignId: meta.campaignId,
          adsetId,
          periodSpend: rec.spend,
          rawBudget: currentBudget,
          periodDays: period,
        }),
        recentChanges: recentChangesFor(platform, 'adset', adsetId, adsetDaily.get(adsetId) ?? []),
        periodSpend: rec.spend,
        drRoas: rec.drRoas,
        brandRoas: rec.brandRoas,
        currentDailyBudget: normalizeCurrentBudget(currentBudget, rec.avgDailySpend),
        recommendedBudget: rec.rawRec,
        performanceBudget: rec.rawRec,
        avgDailySpend: rec.avgDailySpend,
        daysWithSpend: rec.activeDays,
        adjustedBudget: rec.rawRec,
        percentChange: 0,
        hasMissingData: rec.hasMissingData,
        modeledRevenuePeriod: rec.modeledRevenue,
        brandValuePeriod: rec.modeledBrandValue,
        shouldPause: false,
        hasUncategorizedAdsets: false,
        // The campaign's full sibling ad set roster, so "apply to entire
        // campaign" on an ad set row cascades to every ad set instead of
        // writing a masked campaign-level record.
        childAdsetIds: adsetsByCampaign.get(meta.campaignId),
        analysisPeriodDays: period,
        dataThroughDate,
        estimatedConversions,
        confidence: confidenceFor(estimatedConversions),
        estimatedConversionsByPeriod,
      });
    } else {
      const spend = computed.spend;
      const brandValue = computed.brandValue;
      const modeledRevenue = computed.modeledRevenue;
      const estimatedConversions = conversionsFor(modeledRevenue, brandValue, null);
      const estimatedConversionsByPeriod = conversionsByPeriod(dataThroughDate, null, modeledForAdsetWindow);
      rows.push({
        entityId: adsetId,
        entityName: meta.name,
        entityType: 'adset',
        measures: computed.measures,
        campaignId: meta.campaignId,
        initiativeType,
        category: null,
        channel: displayName,
        platform,
        canApplyBudget,
        periodSpend: spend,
        drRoas: spend > 0 ? modeledRevenue / spend : 0,
        brandRoas: spend > 0 ? brandValue / spend : 0,
        ...activityFor({
          campaignId: meta.campaignId,
          adsetId,
          periodSpend: spend,
          rawBudget: currentBudget,
          periodDays: period,
        }),
        recentChanges: recentChangesFor(platform, 'adset', adsetId, adsetDaily.get(adsetId) ?? []),
        // Counted in the pool at its current budget like an assigned row
        // (see the unassigned campaign row above).
        currentDailyBudget: normalizeCurrentBudget(currentBudget, computed.daysWithSpend > 0 ? spend / computed.daysWithSpend : 0),
        recommendedBudget: 0,
        performanceBudget: 0,
        avgDailySpend: computed.daysWithSpend > 0 ? spend / computed.daysWithSpend : 0,
        daysWithSpend: computed.daysWithSpend,
        adjustedBudget: 0,
        percentChange: 0,
        hasMissingData: false,
        modeledRevenuePeriod: modeledRevenue,
        brandValuePeriod: brandValue,
        shouldPause: false,
        hasUncategorizedAdsets: false,
        // The campaign's full sibling ad set roster, so "apply to entire
        // campaign" on an ad set row cascades to every ad set instead of
        // writing a masked campaign-level record.
        childAdsetIds: adsetsByCampaign.get(meta.campaignId),
        analysisPeriodDays: period,
        dataThroughDate,
        estimatedConversions,
        confidence: confidenceFor(estimatedConversions),
        estimatedConversionsByPeriod,
      });
    }
  }

  // Dedupe rows with identical names (e.g., YouTube/Google Ads overlap):
  // keep the entity with the most spend
  const seen = new Map<string, number>(); // entityName → index in rows
  const toRemove = new Set<number>();
  for (let i = 0; i < rows.length; i++) {
    const name = rows[i].entityName;
    const prev = seen.get(name);
    if (prev !== undefined) {
      // Keep the one with more spend, mark the other for removal
      if (rows[i].periodSpend > rows[prev].periodSpend) {
        toRemove.add(prev);
        seen.set(name, i);
      } else {
        toRemove.add(i);
      }
    } else {
      seen.set(name, i);
    }
  }
  if (toRemove.size > 0) {
    for (const idx of Array.from(toRemove).sort((a, b) => b - a)) {
      rows.splice(idx, 1);
    }
  }

  // Attach Google bidding context to campaign rows (YouTube rows are Google
  // campaigns too — same campaign id, platform "google").
  for (const row of rows) {
    if (row.platform === 'google' && row.entityType === 'campaign') {
      row.googleBidding = googleBiddingByCampaign.get(row.campaignId);
    }
  }

  return rows;
}

/**
 * The scaling pass over base rows. `selectedIds` are the rows the user has
 * CHECKED; every other categorized row stays at its current budget and, when
 * active, commits that budget to the pool before the checked rows are scaled.
 * null = every row is in (the "all-in" recommendation).
 *
 * `controls` (saved pins, the brand-search policy, the step cap) are applied
 * first: a locked row is unchecked whatever the selection, and a checked
 * row is clamped to its step cap. The page and the API both pass them, so
 * both hold the same rows. Without `controls`, nothing is locked or capped.
 */
export function allocateRows(
  baseRows: RecommendationRow[],
  inputs: BudgetInputs,
  brandSpendPct: number,
  selectedIds: ReadonlySet<string> | null,
  controls?: RowControls,
): AllocationResult {
  const rows = controls ? applyRowControls(baseRows, controls) : baseRows;
  const excludedIds: ReadonlySet<string> = selectedIds
    ? new Set(rows.filter((r) => r.category !== null && !selectedIds.has(r.entityId)).map((r) => r.entityId))
    : new Set();
  return allocateBudgets(rows, inputs, brandSpendPct, excludedIds);
}
